import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CheckCircle, Calendar, Package, Heart, Home, Eye, ArrowLeft } from 'lucide-react'

interface LocationState {
  orderNumber?: string
  customerInfo?: {
    name: string
    email: string
    phone: string
  }
  orderTotal?: number
  cartItems?: Array<{
    id: string
    name: string
    price: number
    quantity: number
  }>
}

const OrderSuccessPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as LocationState
  
  // Default values if no state is passed
  const orderNumber = state?.orderNumber || `USP-${Date.now().toString().slice(-6)}`
  const customerInfo = state?.customerInfo || {
    name: 'Sarah Johnson',
    email: 'sarah.johnson@email.com',
    phone: '+1 (555) 123-4567'
  }
  const orderTotal = state?.orderTotal || 155.95
  const cartItems = state?.cartItems || [
    {
      id: '1',
      name: 'Red Rose Bouquet',
      price: 45.99,
      quantity: 1
    },
    {
      id: '2',
      name: 'Spring Mixed Bouquet',
      price: 32.99,
      quantity: 2
    }
  ]

  const [deliveryDate] = useState(() => {
    const date = new Date()
    date.setDate(date.getDate() + 2) // 2 days from now
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
  })

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  }

  const checkmarkVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 15,
        delay: 0.2
      }
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-green-100 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#FE4215] rounded-lg flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">Bloom & Blossom</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">Premium Flower Delivery</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/usp-demo')}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Store</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Success Header */}
          <motion.div
            variants={itemVariants}
            className="text-center"
          >
            <motion.div
              variants={checkmarkVariants}
              className="inline-flex items-center justify-center w-20 h-20 bg-green-100 dark:bg-green-900/20 rounded-full mb-6"
            >
              <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
            </motion.div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Order Confirmed!
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Your payment has been processed successfully
            </p>
          </motion.div>

          {/* Order Summary Card */}
          <motion.div
            variants={itemVariants}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-green-100 dark:border-gray-700 overflow-hidden"
          >
            <div className="bg-green-50 dark:bg-green-900/20 px-6 py-4 border-b border-green-100 dark:border-green-800">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Order Confirmation
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Order #{orderNumber}
              </p>
            </div>
            
            <div className="p-6">
              {/* Customer Information */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Delivery Information
                </h3>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</p>
                      <p className="text-gray-900 dark:text-white">{customerInfo.name}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</p>
                      <p className="text-gray-900 dark:text-white">{customerInfo.email}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Phone</p>
                      <p className="text-gray-900 dark:text-white">{customerInfo.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Expected Delivery</p>
                      <p className="text-gray-900 dark:text-white">{deliveryDate}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                  Order Items
                </h3>
                <div className="space-y-3">
                  {cartItems.map((item) => (
                    <div key={item.id} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-600 last:border-b-0">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">{item.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Quantity: {item.quantity}</p>
                      </div>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">Total</span>
                    <span className="text-xl font-bold text-green-600 dark:text-green-400">
                      ${orderTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Timeline Card */}
          <motion.div
            variants={itemVariants}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-green-100 dark:border-gray-700 p-6"
          >
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-green-600 dark:text-green-400" />
              What's Next?
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">Order Confirmed</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Your payment has been processed successfully</p>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-1">Completed</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                    <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">Preparing Your Order</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Our florists are carefully arranging your flowers</p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">In Progress</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                    <Heart className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 dark:text-white">Out for Delivery</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Your flowers will be delivered on {deliveryDate}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Pending</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button
              onClick={() => navigate('/usp-demo')}
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-[#FE4215] hover:bg-[#E5380F] text-white rounded-lg font-medium transition-colors"
            >
              <Home className="w-5 h-5" />
              <span>Continue Shopping</span>
            </button>
            
            <button
              onClick={() => navigate('/final-confirmation', { 
                state: { 
                  orderNumber, 
                  customerInfo, 
                  orderTotal,
                  paymentMethod: 'Visa ending in 9012',
                  billingAddress: {
                    street: '123 Flower Street',
                    city: 'New York',
                    state: 'NY',
                    zipCode: '10001',
                    country: 'United States'
                  },
                  cartItems 
                } 
              })}
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
            >
              <CheckCircle className="w-5 h-5" />
              <span>View Final Confirmation</span>
            </button>
            
            <button
              onClick={() => {
                // Simulate opening order tracking
                alert(`Order tracking will be available at: https://track.bloomandnlossom.com/${orderNumber}`)
              }}
              className="flex items-center justify-center space-x-2 px-6 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg font-medium transition-colors"
            >
              <Eye className="w-5 h-5" />
              <span>Track Order</span>
            </button>
          </motion.div>

          {/* Additional Information */}
          <motion.div
            variants={itemVariants}
            className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 border border-blue-100 dark:border-blue-800"
          >
            <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">
              Important Information
            </h4>
            <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
              <li>• You will receive an email confirmation at {customerInfo.email}</li>
              <li>• SMS updates will be sent to {customerInfo.phone}</li>
              <li>• Delivery window: 9:00 AM - 6:00 PM</li>
              <li>• Please ensure someone is available to receive your order</li>
            </ul>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}

export default OrderSuccessPage
